import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, Download } from "lucide-react";
import Layout from "../components/ui/Layout";
import { RESUME, skillsList } from "../data/resume";
import { downloadResumeDocx, downloadResumeJson, downloadResumePdf } from "../utils/resumeExport";
import "./Resume.css";

const EXPORT_OPTIONS = [
  { id: "pdf", label: "PDF (.pdf)", hint: "Best for sharing and printing" },
  { id: "docx", label: "Word (.docx)", hint: "Editable document" },
  { id: "json", label: "JSON (.json)", hint: "Structured data" },
];

function ResumeDownloadButtons({ exporting, onExport, className = "resume-download__actions" }) {
  return (
    <div className={className} role="group" aria-label="Download resume">
      <button
        type="button"
        disabled={!!exporting}
        aria-label="Download resume as PDF"
        onClick={() => onExport("pdf")}
      >
        <Download size={16} strokeWidth={2.25} aria-hidden="true" />
        {exporting === "pdf" ? "Preparing PDF…" : "Download PDF"}
      </button>
      <button
        type="button"
        disabled={!!exporting}
        aria-label="Download resume as Word document"
        onClick={() => onExport("docx")}
      >
        <Download size={16} strokeWidth={2.25} aria-hidden="true" />
        {exporting === "docx" ? "Preparing Word…" : "Download Word"}
      </button>
      <button type="button" disabled={!!exporting} aria-label="Download resume as JSON" onClick={() => onExport("json")}>
        <Download size={16} strokeWidth={2.25} aria-hidden="true" />
        Download JSON
      </button>
    </div>
  );
}

const Resume = () => {
  const sheetRef = useRef(null);
  const exportMenuRef = useRef(null);
  const [exporting, setExporting] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!exportOpen) return undefined;
    const onPointer = (event) => {
      if (exportMenuRef.current?.contains(event.target)) return;
      setExportOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setExportOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [exportOpen]);

  const runExport = useCallback(async (kind, fn) => {
    if (exporting) return;
    setExporting(kind);
    try {
      await fn();
    } catch (err) {
      console.error(err);
      window.alert("Download failed. Please try again or use Print to save as PDF.");
    } finally {
      setExporting(null);
    }
  }, [exporting]);

  const onExport = useCallback(
    (kind) => {
      setExportOpen(false);
      if (kind === "pdf") {
        runExport("pdf", () => downloadResumePdf(sheetRef.current));
        return;
      }
      if (kind === "docx") {
        runExport("docx", downloadResumeDocx);
        return;
      }
      if (kind === "json") {
        downloadResumeJson();
      }
    },
    [runExport]
  );

  const { name, headline, contact, summary, experience, education, credentials, skills, selectedWork, meta } =
    RESUME;

  const contactLine = [
    contact.location ? { key: "location", node: contact.location } : null,
    contact.openTo ? { key: "openTo", node: contact.openTo } : null,
    { key: "email", node: <a href={`mailto:${contact.email}`}>{contact.email}</a> },
    {
      key: "website",
      node: (
        <a href={contact.website} target="_blank" rel="noreferrer">
          youngdesign.world
        </a>
      ),
    },
    contact.status ? { key: "status", node: contact.status } : null,
    contact.agentPath
      ? {
          key: "agent",
          node: <a href={contact.agentPath}>{contact.agentLabel || "Talk to Digital Young"}</a>,
        }
      : null,
    contact.languages ? { key: "languages", node: contact.languages } : null,
  ].filter(Boolean);

  const exportBusyLabel =
    exporting === "pdf" ? "Exporting PDF…" : exporting === "docx" ? "Exporting Word…" : "Export";

  return (
    <Layout>
      <div className="resume-page">
        <header className="resume-page__bar">
          <h1 className="resume-page__title">Resume</h1>
          <div className="resume-export" ref={exportMenuRef}>
            <button
              type="button"
              className="resume-export__trigger"
              aria-haspopup="menu"
              aria-expanded={exportOpen}
              disabled={!!exporting && exporting !== "json"}
              onClick={() => setExportOpen((open) => !open)}
            >
              {exportBusyLabel}
              <ChevronDown size={16} strokeWidth={2.25} aria-hidden="true" className={exportOpen ? "is-open" : ""} />
            </button>
            {exportOpen ? (
              <ul className="resume-export__menu" role="menu" aria-label="Export format">
                {EXPORT_OPTIONS.map((option) => (
                  <li key={option.id} role="none">
                    <button
                      type="button"
                      role="menuitem"
                      className="resume-export__item"
                      disabled={!!exporting}
                      onClick={() => onExport(option.id)}
                    >
                      <span className="resume-export__item-label">{option.label}</span>
                      <span className="resume-export__item-hint">{option.hint}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </header>

        <article ref={sheetRef} className="resume-sheet" aria-labelledby="resume-name">
          <header className="resume-sheet__header">
            <h2 id="resume-name" className="resume-sheet__name">
              {name}
            </h2>
            <p className="resume-sheet__headline">{headline}</p>
            <p className="resume-sheet__contact">
              {contactLine.map((item, index) => (
                <React.Fragment key={item.key}>
                  {index > 0 ? " · " : null}
                  {item.node}
                </React.Fragment>
              ))}
            </p>
            {contact.address ? <p className="resume-sheet__address">{contact.address}</p> : null}
          </header>

          {summary ? (
            <section className="resume-section">
              <h2>Summary</h2>
              <p>{summary}</p>
            </section>
          ) : null}

          <section className="resume-section">
            <h2>Experience</h2>
            {experience.map((job) => (
              <div key={`${job.company}-${job.dates}`} className="resume-job">
                <div className="resume-job__head">
                  <p className="resume-job__title">
                    {job.title}, {job.company}
                  </p>
                  <p className="resume-job__dates">{job.dates}</p>
                </div>
                {job.location ? <p className="resume-job__meta">{job.location}</p> : null}
                <ul>
                  {job.bullets.map((bullet) => (
                    <li key={bullet.slice(0, 48)}>{bullet}</li>
                  ))}
                </ul>
              </div>
            ))}
          </section>

          <section className="resume-section">
            <h2>Education</h2>
            {education.map((ed) => (
              <div key={ed.school} className="resume-edu">
                <strong>{ed.school}</strong>
                <span>
                  {ed.degree} · {ed.dates}
                </span>
              </div>
            ))}
          </section>

          {credentials?.length ? (
            <section className="resume-section">
              <h2>Credentials</h2>
              <p>{credentials.join(" · ")}</p>
            </section>
          ) : null}

          <section className="resume-section resume-skills">
            <h2>Skills</h2>
            <p>{skillsList(skills).join(" · ")}</p>
          </section>

          {selectedWork?.length ? (
            <section className="resume-section">
              <h2>Selected work</h2>
              <ul className="resume-work-links">
                {selectedWork.map((item) => (
                  <li key={item.url}>
                    <a href={item.url} target="_blank" rel="noreferrer">
                      {item.title}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <p className="resume-sheet__updated">Last updated {meta.updated}</p>
        </article>

        <aside className="resume-download" aria-labelledby="resume-download-heading">
          <p id="resume-download-heading" className="resume-download__intro">
            Need an offline copy?
          </p>
          <ResumeDownloadButtons exporting={exporting} onExport={onExport} />
        </aside>
      </div>
    </Layout>
  );
};

export default Resume;
